import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card } from '@wundo/ui';
import { SaveStatus } from '@/components/SaveStatus.tsx';
import { ContactOption } from '@/components/onboarding/ContactOption.tsx';
import { ManagerQuestion } from '@/components/onboarding/ManagerQuestion.tsx';
import { NotetakerChoice } from '@/components/onboarding/NotetakerChoice.tsx';
import { useColleagueTicks } from '@/components/onboarding/useColleagueTicks.ts';
import { useManagerAnswer } from '@/components/team/useManagerAnswer.ts';
import { useFirstCalendarSync } from '@/components/onboarding/useFirstCalendarSync.ts';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import { fetchOneOnOneColleagues, type OneOnOneColleagueItem } from '@/lib/colleagues.ts';
import {
  fetchFollowExceptions,
  fetchFollowPreferences,
  saveFollowPreferences,
} from '@/lib/meetingFollow.ts';
import { m } from '@/paraglide/messages.js';

/** As many people as read at a glance. The rest are added on the agenda. */
const SHOWN = 5;

/* Module-level, so the sync watch keeps one identity across renders. */
const COLLEAGUES_KEY = ['oneOnOneColleagues'];

/**
 * The last screen of the calendar branch: whose one-to-ones Wundo prepares.
 *
 * A pick is the standing correction for that person, stored the moment it is
 * made — the kinds are not asked here, so following comes from these picks and
 * nothing else.
 */
export const Route = createFileRoute('/onboarding/colleagues')({
  component: OnboardingColleaguesPage,
});

export function OnboardingColleaguesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });

  const sync = useFirstCalendarSync(COLLEAGUES_KEY);

  const colleagues = useQuery({
    queryKey: [...COLLEAGUES_KEY, user?.id, 'active'],
    queryFn: () => fetchOneOnOneColleagues(user!.id, { hidden: false }),
    enabled: Boolean(user?.id),
  });

  const { data: corrections = [] } = useQuery({
    queryKey: ['meetingFollowExceptions'],
    queryFn: fetchFollowExceptions,
  });

  const {
    data: preferences,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['meetingFollowPreferences'],
    queryFn: fetchFollowPreferences,
  });

  const ticks = useColleagueTicks(corrections);

  const save = useMutation({
    mutationFn: saveFollowPreferences,
    onSuccess: async (saved) => {
      /* The server decides what was stored, so the page follows it. */
      queryClient.setQueryData(['meetingFollowPreferences'], saved);
      await queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
  });

  /* Optional and never pre-filled: an empty answer stores nothing. */
  const [manager, setManager] = useState<string | null>(null);
  const managerAnswer = useManagerAnswer();

  const top = topColleagues(colleagues.data);
  const askManager =
    !sync.pending &&
    user?.manager_question_answered_at === null &&
    (colleagues.data ?? []).length > 0 &&
    !(colleagues.data ?? []).some((colleague) => colleague.relationship === 'manager');

  if (isPending) return null;
  if (isError) {
    return (
      <p className='text-base text-ink-muted'>{m.onboarding_preferences_load_failed()}</p>
    );
  }

  /*
   * The kinds are answered here too, and answered off: this branch follows the
   * people the user picked, so a kind left on would quietly add everybody else.
   */
  const saveChoice = (notetaker: boolean) =>
    save.mutate({
      follow_one_on_ones: false,
      follow_team_meetings: false,
      follow_all: false,
      notetaker,
    });

  /*
   * Recommended on for somebody who never chose. Only shown, not stored: the
   * choice is saved when the user confirms or flips it, so opening the step
   * changes nothing for anybody.
   */
  const notetaker = preferences.configured ? preferences.notetaker : true;

  const confirm = () => {
    saveChoice(notetaker);
    /*
     * Not awaited, like the switch above: the optional question never holds up
     * finishing. Should it fail, it simply stays unanswered and the app asks later.
     */
    if (askManager && manager !== null && user) {
      managerAnswer.mutate({ userId: user.id, choice: manager });
    }
    void navigate({ to: '/' });
  };

  return (
    <Card padding='none' className='mx-auto w-full max-w-120 overflow-hidden'>
      <div className='min-w-0 flex-1 px-9 py-10'>
        <SaveStatus
          pending={save.isPending || ticks.saving}
          failed={save.isError || ticks.failed}
        />

        <h1 className='mb-5 text-xl font-semibold tracking-tight'>
          {m.onboarding_colleagues_title()}
        </h1>

        {sync.pending ? (
          <p className='text-sm text-ink-muted'>{m.onboarding_week_syncing()}</p>
        ) : (
          <ColleaguesToPick
            colleagues={top}
            ticked={ticks.ticked}
            onTick={ticks.toggle}
          />
        )}

        <NotetakerChoice checked={notetaker} onCheckedChange={saveChoice} />

        <p className='mt-4 text-sm text-ink-muted'>
          {m.onboarding_colleagues_agenda_note()}
        </p>

        {askManager && (
          <ManagerQuestion
            colleagues={colleagues.data ?? []}
            value={manager}
            onValueChange={setManager}
          />
        )}

        <div className='mt-7 flex items-center gap-3'>
          {/* Never gated on a pick: somebody with nobody to add is still done. */}
          <Button variant='accent' shape='block' size='lg' onClick={confirm}>
            {m.onboarding_confirm()}
          </Button>
          <Button
            variant='ghost'
            shape='block'
            size='lg'
            onClick={() => void navigate({ to: '/onboarding/calendar' })}
          >
            {m.onboarding_back()}
          </Button>
        </div>
      </div>
    </Card>
  );
}

interface ColleaguesToPickProps {
  colleagues: readonly OneOnOneColleagueItem[];
  ticked: (colleague: OneOnOneColleagueItem) => boolean;
  onTick: (colleague: OneOnOneColleagueItem, follow: boolean) => void;
}

/*
 * The people to tick, or the line saying no 1:1 turned up. Never anybody met
 * only in group meetings: a tick follows a person's 1:1s, so it would do nothing.
 */
function ColleaguesToPick({ colleagues, ticked, onTick }: ColleaguesToPickProps) {
  if (colleagues.length === 0) {
    return <p className='text-sm text-ink-muted'>{m.onboarding_colleagues_empty()}</p>;
  }

  return (
    <div className='flex flex-col gap-2'>
      {colleagues.map((colleague) => (
        <ContactOption
          key={colleague.id}
          name={colleague.label}
          email={colleague.email}
          imageUrl={colleague.profile_image_url}
          checked={ticked(colleague)}
          onCheckedChange={(checked) => onTick(colleague, checked)}
        />
      ))}
    </div>
  );
}

/** The colleagues this user has the most 1:1s with, as the Team page knows them. */
function topColleagues(
  colleagues: OneOnOneColleagueItem[] | undefined,
): OneOnOneColleagueItem[] {
  return [...(colleagues ?? [])]
    .sort((left, right) => right.one_on_one_count - left.one_on_one_count)
    .slice(0, SHOWN);
}
